import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2, CheckCircle, XCircle, Edit2, Key, Plus } from 'lucide-react';
import Swal from 'sweetalert2';
import { useLocation } from 'react-router-dom';

const ManageUsers = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialRoleFilter = searchParams.get('filter') || 'All';

  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    role: 'Employee',
    department: '',
    phoneNumber: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState(initialRoleFilter);

  const allRoles = ['CEO', 'Manager', 'Team Lead', 'Developer', 'Designer', 'Employee', 'Intern'];

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      const res = await axios.get(window.API_URL + '/api/portal/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      await axios.put(`${window.API_URL}/api/portal/admin/users/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      Swal.fire({ icon: 'success', title: `User marked as ${status}`, toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
      fetchUsers();
    } catch (err) {
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
        await axios.delete(`${window.API_URL}/api/portal/admin/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchUsers();
      } catch (err) {
        Swal.fire('Error', 'Failed to delete user', 'error');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        role: formData.role,
        fieldOrDepartment: formData.department,
        department: formData.department,
        phoneNumber: formData.phoneNumber,
      };

      if (editId) {
        await axios.put(`${window.API_URL}/api/portal/admin/users/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire({ icon: 'success', title: 'User Updated', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
      } else {
        payload.password = formData.password;
        await axios.post(window.API_URL + '/api/portal/admin/users', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire({ icon: 'success', title: 'User Created', text: 'Please manually share credentials with the user', timer: 3000 });
      }
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || err.response?.data?.error || 'Failed to save user', 'error');
    }
  };

  const handleResetPassword = async (id) => {
    const { value: newPassword } = await Swal.fire({
      title: 'Reset Password',
      input: 'password',
      inputLabel: 'Enter new password',
      inputPlaceholder: 'New password',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) return 'You need to write something!'
      }
    });

    if (newPassword) {
      try {
        const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
        await axios.put(`${window.API_URL}/api/portal/admin/users/${id}/reset-password`, { newPassword }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire('Success', 'Password has been reset successfully', 'success');
      } catch (err) {
        Swal.fire('Error', 'Failed to reset password', 'error');
      }
    }
  };

  const resetForm = () => {
    setFormData({ fullName: '', email: '', password: '', role: 'Employee', department: '', phoneNumber: '' });
    setEditId(null);
  };

  const openEditModal = (user) => {
    setEditId(user._id);
    setFormData({
      fullName: user.fullName,
      email: user.email,
      role: user.role,
      department: user.fieldOrDepartment || '',
      phoneNumber: user.phoneNumber || '',
      password: '', // Leave empty for edit
    });
    setShowModal(true);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'All' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Manage Users</h2>
        <div className="flex items-center space-x-4">
          <input
            type="text"
            placeholder="Search users..."
            className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-[#f06600] outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-[#f06600] outline-none"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="All">All Roles</option>
            {allRoles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#f06600] hover:bg-[#d05800] text-gray-900 px-4 py-2 rounded-lg flex items-center transition-colors font-medium text-sm">
            <Plus className="w-5 h-5 mr-2" />
            Create User
          </button>
        </div>
      </div>
      
      <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-sm uppercase tracking-wider">
                <th className="p-4 font-medium">Name</th>
                <th className="p-4 font-medium">Role</th>
                <th className="p-4 font-medium">Email</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredUsers.map(user => (
                <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-4 font-medium text-gray-900">{user.fullName}</td>
                  <td className="p-4 text-gray-700">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user.role === 'Intern' ? 'bg-orange-500/10 text-[#f06600]' : 'bg-blue-500/20 text-blue-400'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="p-4 text-gray-600">{user.email}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      user.status === 'Active' ? 'bg-green-500/20 text-green-400' : 
                      user.status === 'Pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <button onClick={() => handleResetPassword(user._id)} className="text-blue-400 hover:bg-blue-500/10 p-2 rounded-lg transition-colors" title="Reset Password">
                      <Key className="w-5 h-5" />
                    </button>
                    <button onClick={() => openEditModal(user)} className="text-[#f06600] hover:bg-orange-500/10 p-2 rounded-lg transition-colors" title="Edit User">
                      <Edit2 className="w-5 h-5" />
                    </button>
                    {user.status !== 'Active' && (
                      <button onClick={() => handleStatusChange(user._id, 'Active')} className="text-green-400 hover:bg-green-500/10 p-2 rounded-lg transition-colors" title="Approve">
                        <CheckCircle className="w-5 h-5" />
                      </button>
                    )}
                    {user.status !== 'Suspended' && (
                      <button onClick={() => handleStatusChange(user._id, 'Suspended')} className="text-yellow-400 hover:bg-yellow-500/10 p-2 rounded-lg transition-colors" title="Suspend">
                        <XCircle className="w-5 h-5" />
                      </button>
                    )}
                    <button onClick={() => handleDelete(user._id)} className="text-red-400 hover:bg-red-500/10 p-2 rounded-lg transition-colors" title="Delete">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-500">No users found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-50/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white shadow-sm rounded-2xl p-6 w-full max-w-md border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{editId ? 'Edit User' : 'Create User'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" placeholder="Full Name" required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none" value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} />
              <input type="email" placeholder="Email Address" required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              {!editId && (
                <input type="password" placeholder="Password" required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
              )}
              <select required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none" value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})}>
                {allRoles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <input type="text" placeholder="Phone Number" required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none" value={formData.phoneNumber} onChange={e => setFormData({...formData, phoneNumber: e.target.value})} />
              <input type="text" placeholder="Department / Field" required className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-gray-900 focus:ring-2 focus:ring-[#f06600] outline-none" value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} />
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-600 text-gray-900 rounded-lg transition-colors">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-[#f06600] hover:bg-[#d05800] text-gray-900 rounded-lg transition-colors">{editId ? 'Save Changes' : 'Create User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;
